import api from "src/services/api";

export interface OpcionCatalogo {
  codigo: string;
  etiqueta: string;
}

export interface RelevamientoOperativoCatalogo {
  cantidad_personas: OpcionCatalogo[];
  tiene_espacio_residuos: OpcionCatalogo[];
  pesa_residuos: OpcionCatalogo[];
  reporte_residuos: OpcionCatalogo[];
  tipos_residuos: OpcionCatalogo[];
  reciclable_mas_generado: OpcionCatalogo[];
  contenedores_diferenciados: OpcionCatalogo[];
  bolsas: OpcionCatalogo[];
  dias_semana: OpcionCatalogo[];
  dias_reciclables: OpcionCatalogo[];
  reciclables_mezclados: OpcionCatalogo[];
  frecuencia_retiran_reciclables: OpcionCatalogo[];
  quien_retira: OpcionCatalogo[];
}

export interface PuntoServicioAsignado {
  id: number;
  ps_nombre: string;
  ps_codigo: string;
}

export interface FiscalCatalogo {
  fiscal_id: number;
  fiscal_cedula: string;
  fiscal_nombre: string;
  cargo: string;
}

export interface ContextoPS {
  ps: { id: number; codigo: string; nombre: string };
  fiscal: { nombre?: string };
  segmento_operativo?: string;
}

export interface MultiOpcion {
  codigo: string;
  otro?: string | null;
}

/**
 * Foto en el payload de envío.
 *  - Nueva (recién seleccionada por el usuario): tiene `data_url` (base64).
 *  - Existente (modo edición, foto que ya está en Cloudinary): tiene `public_id` + `url_secure`.
 *  El backend distingue por la presencia de uno u otro campo.
 */
export interface FotoPayload {
  data_url?: string;
  public_id?: string;
  url_secure?: string;
  formato?: string;
  ancho?: number | null;
  alto?: number | null;
  bytes?: number | null;
  nombre_archivo?: string;
  mime_type?: string;
  orden?: number;
}

/** Foto tal como la devuelve el backend al leer un relevamiento (siempre con public_id + url_secure). */
export interface FotoDetalle {
  id?: number;
  orden?: number;
  public_id: string;
  url_secure: string;
  formato?: string | null;
  ancho?: number | null;
  alto?: number | null;
  bytes?: number | null;
  nombre_archivo?: string | null;
}

export interface RelevamientoOperativoPayload {
  ps_id: number;
  ps_codigo?: string;
  ps_nombre?: string;
  fiscal_asignado_id?: number | null;
  fiscal_asignado_cedula?: string | null;
  fiscal_asignado_nombre?: string | null;
  fecha_relevamiento: string;
  segmento_operativo?: string | null;
  cantidad_personas: string;
  cantidad_personas_otro?: string;
  tiene_espacio_residuos: string;
  pesa_residuos: string;
  pesa_residuos_otro?: string;
  reporte_residuos: string;
  reporte_residuos_otro?: string;
  tipos_residuos: string[] | MultiOpcion[];
  reciclable_mas_generado: string;
  contenedores_diferenciados: string;
  bolsas_generales?: string;
  bolsas_generales_otro?: string;
  dias_retiran_generales?: string;
  bolsas_organicas?: string;
  bolsas_organicas_otro?: string;
  dias_retiran_organicos?: string;
  bolsas_reciclables?: string;
  bolsas_reciclables_otro?: string;
  reciclables_mezclados?: string;
  dias_reciclables?: string;
  retira_reciclables_info?: string;
  frecuencia_retiran_reciclables?: string;
  quien_retira_general?: string[] | MultiOpcion[];
  quien_retira_3b?: string[] | MultiOpcion[];
  material_no_cubierto?: string;
  contexto_cliente?: string;
  link_fotos_externo?: string;
  fotos?: FotoPayload[];
  motivo_edicion?: string;
}

export interface RelevamientoOperativoListItem {
  id: number;
  ps_id: number;
  ps_codigo: string;
  ps_nombre: string;
  fiscal_asignado_nombre: string;
  fecha_relevamiento: string;
  segmento_operativo?: string | null;
  seccion_3: "A" | "B";
  version_actual: number;
  creado_por_usuario: string;
  creado_en: string;
  actualizado_en?: string | null;
}

export interface RelevamientoOperativoDetalle {
  id: number;
  ps_id: number;
  ps_codigo?: string;
  ps_nombre?: string;
  fiscal_asignado_id?: number | null;
  fiscal_asignado_cedula?: string | null;
  fiscal_asignado_nombre?: string | null;
  fecha_relevamiento: string;
  segmento_operativo?: string | null;
  cantidad_personas: string;
  cantidad_personas_otro?: string | null;
  tiene_espacio_residuos: string;
  pesa_residuos: string;
  pesa_residuos_otro?: string | null;
  reporte_residuos: string;
  reporte_residuos_otro?: string | null;
  reciclable_mas_generado: string;
  contenedores_diferenciados: string;
  seccion_3: "A" | "B";
  bolsas_generales?: string | null;
  bolsas_generales_otro?: string | null;
  dias_retiran_generales?: string | null;
  bolsas_organicas?: string | null;
  bolsas_organicas_otro?: string | null;
  dias_retiran_organicos?: string | null;
  bolsas_reciclables?: string | null;
  bolsas_reciclables_otro?: string | null;
  reciclables_mezclados?: string | null;
  dias_reciclables?: string | null;
  retira_reciclables_info?: string | null;
  frecuencia_retiran_reciclables?: string | null;
  material_no_cubierto?: string | null;
  contexto_cliente?: string | null;
  link_fotos_externo?: string | null;
  version_actual: number;
  creado_por_usuario: string;
  creado_por_cargo?: string;
  creado_en: string;
  actualizado_por_usuario?: string | null;
  actualizado_en?: string | null;
  multi: Record<string, MultiOpcion[]>;
  fotos: FotoDetalle[];
}

export interface RelevamientoOperativoHistorialItem {
  id: number;
  version_numero: number;
  snapshot: RelevamientoOperativoDetalle | null;
  editado_por_usuario: string;
  editado_por_cargo?: string;
  editado_en: string;
  motivo?: string | null;
}

const BASE = "relevamiento-operativo";

// Helper: lanza un Error con el mensaje real del backend en lugar del genérico "Request failed with status 4xx".
const lanzarErrorBackend = (error: any): never => {
  const msg =
    error?.response?.data?.error ||
    error?.response?.data?.info ||
    error?.message ||
    "Error de comunicación con el servidor";
  throw new Error(msg);
};

export const obtenerCatalogoRelevamientoOperativo = async (): Promise<RelevamientoOperativoCatalogo> => {
  try {
    const { data } = await api.get(`${BASE}/catalogo`);
    if (data?.error) throw new Error(data.error);
    return data?.datos as RelevamientoOperativoCatalogo;
  } catch (error: any) {
    return lanzarErrorBackend(error);
  }
};

export const buscarPuntosServicioAsignados = async (q?: string): Promise<PuntoServicioAsignado[]> => {
  try {
    const suffix = q ? `?q=${encodeURIComponent(q)}` : "";
    const { data } = await api.get(`${BASE}/puntos-servicio${suffix}`);
    if (data?.error) throw new Error(data.error);
    return (data?.datos || []) as PuntoServicioAsignado[];
  } catch (error: any) {
    return lanzarErrorBackend(error);
  }
};

export const obtenerContextoPS = async (psId: number): Promise<ContextoPS> => {
  try {
    const { data } = await api.get(`${BASE}/contexto?ps_id=${psId}`);
    if (data?.error) throw new Error(data.error);
    return data?.datos as ContextoPS;
  } catch (error: any) {
    return lanzarErrorBackend(error);
  }
};

export const crearRelevamientoOperativo = async (payload: RelevamientoOperativoPayload): Promise<number> => {
  try {
    const { data } = await api.post(`${BASE}/`, payload);
    if (data?.error) throw new Error(data.error);
    return Number(data?.datos?.id || 0);
  } catch (error: any) {
    return lanzarErrorBackend(error);
  }
};

export const actualizarRelevamientoOperativo = async (
  id: number,
  payload: RelevamientoOperativoPayload
): Promise<void> => {
  try {
    const { data } = await api.put(`${BASE}/${id}`, payload);
    if (data?.error) throw new Error(data.error);
  } catch (error: any) {
    lanzarErrorBackend(error);
  }
};

export const obtenerRelevamientoOperativo = async (id: number): Promise<RelevamientoOperativoDetalle> => {
  try {
    const { data } = await api.get(`${BASE}/${id}`);
    if (data?.error) throw new Error(data.error);
    return data?.datos as RelevamientoOperativoDetalle;
  } catch (error: any) {
    return lanzarErrorBackend(error);
  }
};

export interface ListarRelevamientosFiltros {
  ps_codigo?: string;
  fecha_desde?: string;
  fecha_hasta?: string;
  limit?: number;
  offset?: number;
}

export interface ListarRelevamientosResponse {
  datos: RelevamientoOperativoListItem[];
  total: number;
  limit: number;
  offset: number;
}

export const listarRelevamientosOperativos = async (
  filtros: ListarRelevamientosFiltros = {}
): Promise<ListarRelevamientosResponse> => {
  const params = new URLSearchParams();
  if (filtros.ps_codigo) params.set("ps_codigo", filtros.ps_codigo);
  if (filtros.fecha_desde) params.set("fecha_desde", filtros.fecha_desde);
  if (filtros.fecha_hasta) params.set("fecha_hasta", filtros.fecha_hasta);
  if (filtros.limit !== undefined) params.set("limit", String(filtros.limit));
  if (filtros.offset !== undefined) params.set("offset", String(filtros.offset));
  const suffix = params.toString() ? `?${params.toString()}` : "";
  const { data } = await api.get(`${BASE}/listar${suffix}`);
  if (data?.error) throw new Error(data.error);
  return {
    datos: (data?.datos || []) as RelevamientoOperativoListItem[],
    total: Number(data?.total || 0),
    limit: Number(data?.limit || filtros.limit || 20),
    offset: Number(data?.offset || filtros.offset || 0),
  };
};

export const obtenerHistorialRelevamiento = async (
  id: number
): Promise<RelevamientoOperativoHistorialItem[]> => {
  const { data } = await api.get(`${BASE}/${id}/historial`);
  if (data?.error) throw new Error(data.error);
  return (data?.datos || []) as RelevamientoOperativoHistorialItem[];
};

export interface EstadisticasRepro {
  total_asignados: number;
  con_relevamiento: number;
}

export const obtenerEstadisticasRepro = async (): Promise<EstadisticasRepro> => {
  try {
    const { data } = await api.get(`${BASE}/stats`);
    if (data?.error) throw new Error(data.error);
    return data?.datos as EstadisticasRepro;
  } catch (error: any) {
    return lanzarErrorBackend(error);
  }
};
